import React from "react";
import { GetEntryAPI, UpdateAPI } from "../services";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LanguageLoader } from '../assets/languages';
import Fields from "../components/features/Fields";
import FieldContainer from "../components/features/FieldContainer";
import Comment from "../components/features/Comment";
import Subpanel from "../components/features/Subpanel";
import Table from "../components/features/Table";
import { PanelSkeleton } from "../components/layouts/Skeleton";
import AdditionalButtons from "../components/features/AdditionalButtons";

const DetailView = () =>
{
	const settings = useSelector((state) => state.settings);
	const user = useSelector((state) => state.current_user);
	const preferences = useSelector((state) => state.preferences);
	const language = LanguageLoader(preferences.language);
	const {module, id} = useParams();
	const location = useLocation();
	const subpanel = (location.pathname.indexOf('/subpanel/') !== -1 || (location.state && location.state.subpanel)) ? true : false;
	const definition = settings.definitions[subpanel ? 'subpanels' : 'modules'][module];
	const fields = definition.fields;
	const [dataEntry, SetDataEntry] = useState({});
	const [customePage, SetCustomePage] = useState({});
	const [customePanels, SetCustomePanels] = useState([]);
	const [permissions, SetPermissions] = useState({});
	const [loading, SetLoading] = useState(true);
	const navigate = useNavigate();
	const disableFieldsPerModuleDetail =
		(settings.disable_fields_per_module &&
		settings.disable_fields_per_module[module] &&
		Array.isArray(settings.disable_fields_per_module[module].detail))
			? settings.disable_fields_per_module[module].detail
			: [];
	let subpanels = settings.subpanels[module] || {};
	// sort subpanels
	if (Object.keys(settings.definitions['subpanels']).length > 0)
	{
		const desiredOrder = Object.keys(settings.definitions.subpanels); 
		const sortedEntries = Object.entries(subpanels).sort((a, b) => {
			const indexA = desiredOrder.indexOf(a[1]);
			const indexB = desiredOrder.indexOf(b[1]);
			if (indexA === -1) return 1;
			if (indexB === -1) return -1;
			return indexA - indexB;
		});
		subpanels = Object.fromEntries(sortedEntries);
	}
	
	useEffect(() =>
	{
		window.scrollTo(0, 0);
		SetLoading(true);
		GetEntry();
	// eslint-disable-next-line
	}, [module, id]);

	const GetEntry = async () =>
	{
		try
		{
			if (id && fields)
			{
				var selectedFields = ['id'];
				for(let field in fields)
				{
					selectedFields.push(field);
					if(fields[field]['related_to'] && fields[field]['related_to'] !== '')
					{
						selectedFields.push(fields[field]['related_to']);
					}
				}
				if(module === 'Cases' && selectedFields.indexOf('status') === -1)
				{
					selectedFields.push('status');
				}
				const data = { id: id, module: module, selected_fields: selectedFields }
				const APIResponse = await GetEntryAPI(data);
				if (APIResponse)
				{
					// check if record is hidden.
					if(APIResponse[0].Hidden)
					{
						if(APIResponse[0].Hidden == true)
						{
							navigate(`/list/${module}`);
							return;
						}
					}
					SetDataEntry(APIResponse[0]);
					SetCustomePage(APIResponse['custome_page_module']);
					SetCustomePanels(Array.isArray(APIResponse['customePanels']) ? APIResponse['customePanels'] : []);
					SetPermissions(APIResponse['permissions']);
					SetLoading(false);
				}
			}
			else
			{
				SetDataEntry({});
				SetCustomePanels([]);
				SetLoading(false);
			}
		}
		catch (ex){}
	}

	const RenderPanelByType = (panel) =>
	{
		if(panel.type === 'survey_answers')
		{
			return RenderSurveyAnswerSections(panel.sections);
		}
		if(panel.type === 'table')
		{
			const rows = Array.isArray(panel.data) ? panel.data : [];
			const columnKeys = GetPanelTableColumnKeys(rows);
			const columns = GetPanelTableColumns(panel, columnKeys);
			return <Table header={columns} className='responsive'>
				{rows.map((row, index) => <tr key={index}>
					{columnKeys.map((columnKey) => <td key={columnKey}>{RenderPanelCellValue(row, columnKey)}</td>)}
				</tr>)}
			</Table>;
		}
		return null;
	}

	const RenderSurveyAnswerSections = (sections) =>
	{
		const items = Array.isArray(sections) ? sections : [];
		if(!items.length)
		{
			return <div>{language.NO_DATA}</div>;
		}
		return <div className="flex flex-wrap w-full">
			{items.map((section, sectionIndex) =>
			{
				if(section.type === 'flat')
				{
					return <div key={`flat-${sectionIndex}`} className="flex flex-wrap w-full">
						{RenderSurveyAnswerFields(section.questions)}
					</div>;
				}
				const subcategories = Array.isArray(section.subcategories) ? section.subcategories : [];
				return <div key={section.id || sectionIndex} className="survey-category">
					<div className="survey-category-title">{section.name}</div>
					{subcategories.length ? subcategories.map((subcategory) => <div key={subcategory.id} className="survey-subcategory">
						<div className="survey-subcategory-title">{subcategory.name}</div>
						<div className="flex flex-wrap">
							{RenderSurveyAnswerFields(subcategory.questions)}
						</div>
					</div>) : <div className="flex flex-wrap">
						{RenderSurveyAnswerFields(section.questions)}
					</div>}
				</div>;
			})}
		</div>;
	}

	const RenderSurveyAnswerFields = (questions) =>
	{
		return (questions || []).map((question) => <FieldContainer
			key={question.id || question.index}
			className='w-full'
			label={`${question.index}. ${question.question}`}
		>
			{question.description ? <div className="mt-1 text-xs opacity-80">{question.description}</div> : null}
			<div className="value mt-2">
				{RenderSurveyAnswerValue(question)}
			</div>
		</FieldContainer>);
	}

	const RenderSurveyAnswerValue = (question) =>
	{
		const type = question.field_type;
		if(type === 'checkbox')
		{
			return <input type="checkbox" className="form-control" disabled checked={question.answer === 'Yes'} />;
		}
		if(type === 'NPS' || type === 'CES' || type === 'CSAT')
		{
			const max = type === 'NPS' ? 10 : (type === 'CES' ? 7 : 5);
			const selected = Number(question.answer) || 0;
			return <div className="flex flex-wrap items-center justify-end" style={{direction:'ltr'}}>
				{Array.from({length: max}, (_, starIndex) => starIndex + 1).map((rate) => <i
					key={rate}
					className="fa fa-star"
					style={{fontSize:20, margin:'0 2px', color: selected >= rate ? '#f5b301' : '#c5c5c5'}}
				/>)}
			</div>;
		}
		const answer = question.answer;
		const text = (answer !== undefined && answer !== null && answer !== '') ? answer : '-';
		return <div className="survey-answer-box">{Array.isArray(text) ? text.join(', ') : text}</div>;
	}

	const GetPanelTableColumnKeys = (rows) =>
	{
		const ignoredKeys = ['field_type'];
		const keys = [];
		rows.forEach((row) =>
		{
			if(!row || typeof row !== 'object')
			{
				return;
			}
			Object.keys(row).forEach((key) =>
			{
				if(!ignoredKeys.includes(key) && !keys.includes(key))
				{
					keys.push(key);
				}
			});
		});
		return keys;
	}

	const GetPanelTableColumns = (panel, columnKeys) =>
	{
		const headers = {};
		columnKeys.forEach((key) =>
		{
			headers[key] = GetPanelColumnLabel(panel, key);
		});
		return headers;
	}

	const GetPanelColumnLabel = (_panel, key) =>
	{
		if(language[key])
		{
			return language[key];
		}
		const upperKey = key.toUpperCase();
		if(language[upperKey])
		{
			return language[upperKey];
		}
		if(language.FIELDS && language.FIELDS[upperKey])
		{
			return language.FIELDS[upperKey];
		}
		return key.replaceAll('_', ' ');
	}

	const GetPanelTitle = (panel) =>
	{
		if(module === 'Surveys_Response' && (panel.type === 'table' || panel.type === 'survey_answers'))
		{
			return language.SURVEY_RESPONSES;
		}
		return '';
	}

	const RenderPanelAnswerByFieldType = (row) =>
	{
		if(!row || typeof row !== 'object')
		{
			return '';
		}
		if(row.field_type === 'attachment' && Array.isArray(row.answer))
		{
			return row.answer.length ? row.answer.join(', ') : '-';
		}
		if(row.field_type === 'checkbox')
		{
			if (row.answer === 'Yes')
			{
				return <input type="checkbox"className="form-control" disabled checked/>;
			}
			return <input type="checkbox"className="form-control" disabled/>;
		}
		return (row.answer !== undefined && row.answer !== null && row.answer !== '') ? row.answer : '-';
	}

	const RenderPanelCellValue = (row, key) =>
	{
		if(!row || typeof row !== 'object')
		{
			return '-';
		}
		if(key === 'answer') // this is just for surveyResponse
		{
			return RenderPanelAnswerByFieldType(row);
		}
		const value = row[key];
		if(Array.isArray(value))
		{
			return value.length ? value.join(', ') : '-';
		}
		return (value !== undefined && value !== null && value !== '') ? value : '-';
	}

	const HandleApproval = async (actionType) =>
	{
		const module_name = 'Approval';
		let data = {};
		if(actionType === 'accept')
		{
			data = {status : 'Approved'}
		}
		else if(actionType === 'reject')
		{
			data = {status : 'Rejected'}
		}
		else
		{
			return true;
		}
		const APIResponse = await UpdateAPI(data, module_name, dataEntry.approval.id);
		if(APIResponse.id)
		{
			SetDataEntry((prevData) => ({
				...prevData,
				approval: {
				  ...prevData.approval,
				  status: actionType === 'accept' ? language.APPROVED : language.REJECTED,
				  status_key: actionType,
				},
			  }));
		}
	}

	if(!definition.access.view)
	{
		return <div className='wrapper flex flex-wrap justify-between items-center'>
			<div>{language.ERROR.NON_ACCESSIBLE}</div>
			<Link to='/' className="btn -mt-1">بازگشت به صفحه اصلی</Link>
		</div>;
	}
	const isPartner = Number(user.is_partner) === 1;
	const approvalModel = dataEntry?.approval?.model;
	const showSurveyQuestionsButton = module === 'Surveys_Response' && definition.access.edit && dataEntry.status !== 'completed';
	const showEditButton = definition.access.edit && module !== 'Surveys_Response';
	const blockedCommentStatuses = (module === 'Cases' && Array.isArray(settings.case_comment_disabled_statuses)) ? settings.case_comment_disabled_statuses.map(String) : [];
	const commentStatusKnown = blockedCommentStatuses.length === 0 || !loading;
	const commentLocked = commentStatusKnown && blockedCommentStatuses.includes(String(dataEntry.status ?? ''));
	const canWriteComment = Boolean(definition.comment) && commentStatusKnown && !commentLocked;
	return (
		<>
		{ (dataEntry?.approval && ((approvalModel === "Customer_Approval" && !isPartner) || (approvalModel === "Business_Approval" && isPartner))) ?
			<div className='wrapper'>
				<div className="flex flex-wrap">
					<div className="Field_Container mb-0">
						<div className="flex">
							<label>{language.APPROVAL_STATUS}</label>
						</div>
						<div className="value">{dataEntry.approval.status}</div>
					</div>
					{dataEntry.approval.status_key === 'Pending' ?
					<div className="Field_Container mb-0 text-end">
						<button onClick={() => HandleApproval('accept')} className='btn btn-secondary mb-2'>{language.APPROVE}</button>
						<button onClick={() => HandleApproval('reject')} className='btn bg-transparent border-danger text-danger mb-2'>{language.DECLINE}</button>
					</div>
					: null }
				</div>
			</div>
		: null }
		{loading ? <PanelSkeleton/> :
		<div className='wrapper'>
			<div className='title flex justify-between w-full'>
				<h6>{language["DETAIL_VIEW"] + ' ' + definition.label[preferences.language]}</h6>
				<div className="flex">
					<AdditionalButtons module={module} id={id} dataEntry={dataEntry}/>
					<Link to={`/list/${module}`} className='btn btn-secondary mb-2'>{language.AUTHENTICATION.RETURN}</Link>
					{module == 'Opportunities' && definition.access.create ? <button onClick={() => navigate('/edit/'+definition.module+'/'+id, {state:{'subpanel': subpanel, 'isDuplicate': true}})} className='btn-primary mb-2'>{language["CREATE_DUPLICATE"]}</button> : null} 
					{module == 'Surveys_Response' && definition.access.create ? ( <button onClick={() => navigate(`/detail/${dataEntry.parent_type}/${dataEntry.parent_id}`)} className='btn btn-secondary mb-2' >{language['RELATED_TO_RECORD']}</button> ) : null}
					{showSurveyQuestionsButton ? <button onClick={() => navigate('/survey-response/'+id)} className='btn-primary mb-2'>{language.ANSWER_QUESTIONS}</button> : null}
					{showEditButton ? (
					<button
					onClick={() => {
						const hasCustomPage =
						Array.isArray(customePage) &&
						customePage.length > 0 &&
						customePage.includes(definition.module);
						if (hasCustomPage) {
						navigate(`/dynamic/Create${definition.module}?id=${id}`, { state: { subpanel } });
						} else {
						navigate(`/edit/${definition.module}/${id}`, { state: { subpanel } });
						}
					}}
					className="btn-primary mb-2"
					>
					{language["EDIT"]}
					</button>
					) : null}
				</div>
			</div>
			<div className="flex flex-wrap">
				{Object.keys(fields).map((field, index) =>
				{
					if(settings.hidden_fields.includes(field) || disableFieldsPerModuleDetail.includes(field))
					{
						return null;
					}
					return <Fields key={index}
						subpanel={subpanel}
						module={module}
						field={field}
						value={dataEntry[field]}
						editable={false}
						related_to={fields[field]['related_to'] ? dataEntry[fields[field]['related_to']] : null}
						related_module={fields[field]['module_field'] ? dataEntry[fields[field]['module_field']] : null}
					/>
				})}
			</div>
		</div>
		}
		{customePanels.length ? <div className='wrapper'>
			{/* <div className='title flex justify-between w-full'>
				<h6>{language.MORE_INFORMATION}</h6>
			</div> */}
			{customePanels.map((panel, index) => <div key={index}>
				{GetPanelTitle(panel) ? <div className='flex justify-between items-center w-full border-b border-disabled mb-4 pb-1 min-h-[36px]'><h6 className='opacity-70 text-sm'>{GetPanelTitle(panel)}</h6></div> : null}
				{RenderPanelByType(panel)}
			</div>)}
		</div> : null}
		<Comment editor={canWriteComment} />
		{subpanels && Object.entries(subpanels).map((item, index) => item[1] ? <Subpanel key={index} data={item}/> : null )}
		</>
	);
}

export default DetailView;
